import {Injectable} from "@angular/core";
import {Platform} from "ionic-angular";
import {Storage} from "@ionic/storage";
import {Observable} from "rxjs/Observable";
import {Observer} from "rxjs/Observer";
import {CODE_READER_IS_CONNECTED, CODE_READER_SET_CONFIG} from "./reader.constants";
import {ReaderDevice} from "../models/reader-model/readerDevice";
import {ReaderConnectionResponse} from "../models/reader-model/readerConnectionResponse";
import {TagReadResponse} from "../models/reader-model/tagReadResponse";
import {TagReadBarcodeResponse} from "../models/reader-model/tagReadBarcodeResponse";
import {deserialize} from "serializer.ts/Serializer";
import {SimulatorService} from "../providers/simulator.service";
import {BarcodeScanner} from "@ionic-native/barcode-scanner";
import {BARCODE_TYPE_CODE_128, ReaderMode, STORAGE_READER_SOUND_LEVEL} from "../app/app.constants";
import {isUndefined} from "ionic-angular/util/util";
import {ReaderThingConfiguration} from "../models/ReaderThingConfiguration";
import {VizixService} from './vizix.service';

@Injectable()
export class ReaderService {

  private tag = '[READER_SERVICE]';

  public continueWithoutReader;

  public readerInformation: ReaderConnectionResponse;

  private triggerListenerObservable: Observable<any>;
  private triggerListenerObserver: Observer<any>;

  private tagResponseMultiRfidObservable: Observable<any>;
  private tagResponseMultiRfidObserver: Observer<any>;

  private tagResponseSingleRfidObservable: Observable<any>;
  private tagResponseSingleRfidObserver: Observer<any>;

  private tagResponseGeigerObservable: Observable<any>;
  private tagResponseGeigerObserver: Observer<any>;

  public isInventoryMode = false;
  public isGeigerMode = false;
  public isMultiRfidMode = false;
  public isSingleRfidMode = false;

  private selectedTagForGeiger: string = '';
  private tagListForGeiger: Array<string> = [];

  public tagProximity = 0;
  public isTriggerPulled = false;
  currentSoundLevel: number;
  private timeOut: number = 45000;

  private STOP_TAG: string = '000';
  private isCordovaEnabled = false;

  constructor(public platform: Platform,
              public storage: Storage,
              private simulatorService: SimulatorService,
              private _vizixService: VizixService,
              private barcodeScanner: BarcodeScanner) {
    this.initTriggerListener();
    this.continueWithoutReader = false;
  }

  initFromDevice() {
    console.log(this.tag, ' Cordova is Enabled');
    this.isCordovaEnabled = true;
  }

  initTriggerListener() {
    this.triggerListenerObservable = Observable.create((observer: Observer<any>) => {
      this.triggerListenerObserver = observer;

      if (!this.simulatorService.isSimulatorEnabled) {
        if (this.isCordovaEnabled) {
          try {
            RFIDInterface.setTriggerListener(
              (result) => {
                console.log(this.tag, ' Response from trigger listener', result);
                this.isTriggerPulled = result != 0;
                observer.next(result != 0);
              },
              () => {
                console.log(this.tag, ' Error setting trigger listener');
              }
            );
          } catch (error) {
            console.log(this.tag, ' Cordova not available or plugins are not setup correctly');
          }
        } else {
          this.cordovaNotAvailable(null);
        }
      }
    });
  }

  setTriggerListener(): Observable<any> {
    return this.triggerListenerObservable;
  }

  availableDeviceList(): Observable<any> {
    return Observable.create((observer: Observer<any>) => {
      if (this.isCordovaEnabled) {
        try {
          RFIDInterface.listPairedDevices(
            (result) => {
              observer.next(JSON.parse(result));
              // observer.complete();
            },
            () => {
              observer.error('Error');
              // observer.complete();
            }
          );
        } catch (error) {
          observer.error('Cordova not available or plugins are not setup correctly');
          // observer.complete();
        }
      } else {
        this.cordovaNotAvailable(observer);
      }
    });
  }

  connect(device: ReaderDevice, configurationThing: any): Observable<any> {
    console.log(this.tag, ' Connection Attempt');
    return Observable.create((observer: Observer<any>) => {
      if (this.isCordovaEnabled) {
        try {
          console.log(" thing configuraton ----");
          console.log(configurationThing);
          RFIDInterface.connect(device, configurationThing,
            result => {

              /*
                Partial: RFID SDK connected
                Partial: RFID configured
                Done: RFID and Barcode Ready
               */

              console.log(this.tag, "Response from connect", result);
              try {
                let readerConnectionResponse = deserialize<ReaderConnectionResponse>(ReaderConnectionResponse, JSON.parse(result));

                if (readerConnectionResponse.name) {
                  // Done
                  // Save the object locally
                  this.readerInformation = readerConnectionResponse;
                  observer.next(readerConnectionResponse);
                  observer.complete();
                }
              } catch (e) {
                // Partial
                //   observer.error("Reader couldn't connect, code: 0");
                //   observer.complete();
                observer.next(result);
              }
            },
            error => {
              console.log(this.tag, "Response from connect 1", error);
              observer.error(JSON.parse(error));
              observer.complete();
            });
        } catch (error) {
          observer.error('Cordova not available or plugins are not setup correctly');
          observer.complete();
        }
      } else {
        this.cordovaNotAvailable(observer);
      }

    }).timeout(this.timeOut);
  }

  pairAndConnect(device: string, configurationThing: any): Observable<any> {
    console.log(this.tag, ' Connection Attempt');
    return Observable.create((observer: Observer<any>) => {
      if (this.isCordovaEnabled) {
        try {
          RFIDInterface.pairAndConnect(device, configurationThing,
            result => {
              console.log(this.tag, result);
              let readerConnectionResponse = deserialize<ReaderConnectionResponse>(ReaderConnectionResponse, JSON.parse(result));

              // Save the object locally
              this.readerInformation = readerConnectionResponse;

              observer.next(readerConnectionResponse);
              observer.complete();
            },
            error => {
              console.log(this.tag, error);
              observer.error(JSON.parse(error));
              observer.complete();
            });
        } catch (error) {
          observer.error('Cordova not available or plugins are not setup correctly');
        }
      } else {
        this.cordovaNotAvailable(observer);
      }
    }).timeout(this.timeOut);
  }

  disconnect(): Observable<any> {
    return Observable.create((observer: Observer<any>) => {
      if (this.isCordovaEnabled) {
        try {
          RFIDInterface.disconnect(
            result => {
              let response = JSON.parse(result);
              observer.next(response);
              console.log(this.tag, response);
              observer.complete();
            },
            error => {
              observer.error(JSON.parse(error));
              observer.error('Reader bad response');
              observer.next(error);
              observer.complete();
            });
        } catch (error) {
          observer.error('Cordova not available or plugins are not setup correctly');
          observer.next(error);
        }
      } else {
        this.cordovaNotAvailable(observer);
      }
    });
  }

  initMultipleScan(): Observable<any> {
    this.switchMode(ReaderMode.MULTI);

    if (this.simulatorService.isSimulatorEnabled) {
      //SimulatorConfiguration
      this.simulatorService.stopSimulator();
    }

    this.tagResponseMultiRfidObservable = Observable.create((observer: Observer<TagReadResponse>) => {
      this.tagResponseMultiRfidObserver = observer;

      if (!this.simulatorService.isSimulatorEnabled) {
        if (this.isCordovaEnabled) {
          //From plugin
          try {
            // this.setBeepSound(0).subscribe();

            RFIDInterface.initMultipleRead(
              result => {
                if (this.isTriggerPulled) {
                  let parsedResult = JSON.parse(result);
                  if (parsedResult.value) {
                    let deserializedResponse = deserialize<TagReadResponse>(TagReadResponse, parsedResult);
                    deserializedResponse.time = this._vizixService.vizixTime();
                    observer.next(deserializedResponse);
                  }
                }
                /*else if (parsedResult.mode || parsedResult.mode == '') {
                 this.triggerListenerObserver.next(parsedResult);
                 }*/
              },
              error => {
                observer.error(JSON.parse(error));
                observer.complete();
              });
          } catch (error) {
            observer.error('Cordova not available or plugins are not setup correctly');
            observer.complete();
          }
        } else {
          this.cordovaNotAvailable(observer);
        }
      } else {
        //From simulator
        this.simulatorService.getTagObservable().subscribe(
          (tagResponse: TagReadResponse) => {
            tagResponse.time = this._vizixService.vizixTime();
            //console.log(this.tag, "Simuate read: " + tagResponse.value);
            observer.next(tagResponse);
          }, (error) => {
            console.log(this.tag, ' ERROR SimulatorConfiguration RFID', error);
            observer.error('ERROR SimulatorConfiguration RFID');
            observer.complete();
          }
        );
      }
    });

    return this.tagResponseMultiRfidObservable;
  }

  initInventory(tagList?: Array<string>): Observable<any> {
    console.log(this.tag, "init inventory");
    if (this.simulatorService.isSimulatorEnabled) {
      //SimulatorConfiguration
      this.simulatorService.stopSimulator();
    }

    if (tagList) {
      this.tagListForGeiger = tagList;
    }

    this.switchMode(ReaderMode.INVENTORY);

    this.tagResponseMultiRfidObservable = Observable.create((observer: Observer<TagReadResponse>) => {
      this.tagResponseMultiRfidObserver = observer;

      if (!this.simulatorService.isSimulatorEnabled) {
        if (this.isCordovaEnabled) {
          // From plugin
          try {
            RFIDInterface.initInventory(
              result => {
                if (this.isTriggerPulled) {
                  let parsedResult = JSON.parse(result);
                  if (parsedResult.value) {
                    let deserializedResponse = deserialize<TagReadResponse>(TagReadResponse, parsedResult);
                    deserializedResponse.time = this._vizixService.vizixTime();
                    observer.next(deserializedResponse);
                  }
                }
                /*else if (parsedResult.mode || parsedResult.mode == '') {
                 this.triggerListenerObserver.next(parsedResult);
                 }*/
              },
              error => {
                observer.error(JSON.parse(error));
                // observer.complete();
              });
          } catch (error) {
            observer.error('Cordova not available or plugins are not setup correctly');
            observer.complete();
          }
        } else {
          this.cordovaNotAvailable(observer);
        }
      } else {
        //From simulator
        this.simulatorService.getTagObservable().subscribe(
          (tagResponse: TagReadResponse) => {
            tagResponse.time = this._vizixService.vizixTime();
            console.log(this.tag, ' Simulate read: ' + tagResponse.value);
            observer.next(tagResponse);
          }, (error) => {
            console.log(this.tag, ' ERROR SimulatorConfiguration RFID', error);
            observer.error('ERROR SimulatorConfiguration RFID');
            observer.complete();
          }
        );
      }
    });

    return this.tagResponseMultiRfidObservable;
  }

  getBeepSoundStorage(): Promise<number> {
    console.log(this.tag, "get beep sound");

    return new Promise<number>((resolve, reject) => {
      this.storage.get(STORAGE_READER_SOUND_LEVEL).then((levelSound) => {
        console.log(this.tag, ' Sound level from storage', levelSound);
        if (!isUndefined(levelSound) && levelSound != null) {
          resolve(levelSound);
        } else {
          reject();
        }
      }, () => {
        reject();
      });
    });
  }

  setBeepSound(soundLevel): Observable<any> {
    console.log(this.tag, "set beep sound");

    this.currentSoundLevel = soundLevel;

    return Observable.create((observer: Observer<any>) => {
      if (this.isCordovaEnabled) {
        try {
          RFIDInterface.setSoundLevel(soundLevel,
            result => {
              console.log(this.tag, ' Sound Level set to', soundLevel);
              observer.next(result);
              observer.complete();
            },
            error => {
              observer.error(JSON.parse(error));
              observer.complete();
            });
        } catch (error) {
          observer.error('Cordova not available or plugins are not setup correctly');
          observer.complete();
        }
      } else {
        this.cordovaNotAvailable(observer);
      }
    });
  }


  setBeepSoundNoStorageValue(soundLevel): Observable<any> {
    this.currentSoundLevel = soundLevel;
    return Observable.create((observer: Observer<any>) => {
      if (this.isCordovaEnabled) {
        try {
          RFIDInterface.setSoundLevel(soundLevel,
            result => {
              console.log(this.tag, ' Sound Level set to', soundLevel);
              observer.next(result);
              observer.complete();
            },
            error => {
              observer.error(JSON.parse(error));
              observer.complete();
            });
        } catch (error) {
          observer.error('Cordova not available or plugins are not setup correctly');
          observer.complete();
        }
      } else {
        this.cordovaNotAvailable(observer);
      }
    });
  }

  setStorageSoundVolume(soundLevelValue) {
    this.storage.set(STORAGE_READER_SOUND_LEVEL, soundLevelValue);
  }

  isConnected(disableSimulatorMock ?: boolean): Observable<any> {
    console.log(this.tag, "is connected");

    return Observable.create((observer: Observer<any>) => {

      if (!this.simulatorService.isSimulatorEnabled || disableSimulatorMock) {
        if (this.isCordovaEnabled) {
          //Reader
          try {
            RFIDInterface.isConnected(
              result => {
                observer.next(result);
                observer.complete();
              },
              error => {
                observer.error(JSON.parse(error));
                observer.complete();
              });
          } catch (error) {
            observer.error('Cordova not available or plugins are not setup correctly');
            observer.complete();
          }
        } else {
          this.cordovaNotAvailable(observer);
        }
      } else {
        //SimulatorConfiguration
        observer.next(true);
        observer.complete();
      }
    });
  }


  stop_reader_listener(): Observable<any> {
    console.log(this.tag, "stop read listener");

    return Observable.create((observer: Observer<any>) => {
      if (this.isCordovaEnabled) {
        try {
          RFIDInterface.stopScan(
            result => {
              observer.next(result);
              observer.complete();
            },
            error => {
              observer.error(JSON.parse(error));
              observer.complete();
            });
        } catch (error) {
          observer.error('Cordova not available or plugins are not setup correctly');
          observer.complete();
        }
      } else {
        this.cordovaNotAvailable(observer);
      }
    });
  }


  setReaderPower(power, mode): Observable<any> {
    console.log(this.tag, "set reader power");

    return Observable.create((observer: Observer<any>) => {

      if (!this.simulatorService.isSimulatorEnabled) {
        if (this.isCordovaEnabled) {
          //Reader
          try {
            RFIDInterface.setPower(power, mode,
              result => {
                observer.next(result);
                observer.complete();
              },
              error => {
                observer.error(JSON.parse(error));
                observer.complete();
              });
          } catch (error) {
            observer.error('Cordova not available or plugins are not setup correctly');
            observer.complete();
          }
        } else {
          this.cordovaNotAvailable(observer);
        }
      } else {
        //SimulatorConfiguration
        observer.next(true);
        observer.complete();
      }
    });
  }


  initBarcodeScan(): Observable<TagReadBarcodeResponse> {
    console.log(this.tag, ' Barcode is initializing');
    return Observable.create((observer: Observer<TagReadBarcodeResponse>) => {
      if (this.isCordovaEnabled) {
        try {
          RFIDInterface.initBarcode(
            result => {
              let deserializedResponse = deserialize<TagReadBarcodeResponse>(TagReadBarcodeResponse, JSON.parse(result));
              observer.next(deserializedResponse);
            },
            error => {
              observer.error(JSON.parse(error));
              observer.complete();
            });
        } catch (error) {
          observer.error('Cordova not available or plugins are not setup correctly');
          observer.complete();
        }
      } else {
        this.cordovaNotAvailable(observer);
      }
    });
  }

  initBarcodeCameraScan(): Promise<TagReadBarcodeResponse> {
    return new Promise<TagReadBarcodeResponse>((resolve, reject) => {
      try {
        this.barcodeScanner.scan({
          showTorchButton: true,
          resultDisplayDuration: 600,
          orientation: 'portrait'
        }).then((barcodeData) => {
          if (!barcodeData.cancelled) {
            resolve(new TagReadBarcodeResponse(this._vizixService.vizixTime(), BARCODE_TYPE_CODE_128, String(barcodeData.text)));
          } else {
            reject('Cancelled by user');
          }
        }, (err) => {
          reject(err);
        });
      } catch (error) {
        reject('Cordova not available or plugins are not setup correctly');
      }
    });
  }

  initSingleScan(): Observable<any> {
    console.log(this.tag, "init single scan");

    this.switchMode(ReaderMode.SINGLE);

    return Observable.create((observer: Observer<TagReadResponse>) => {
      //this.tagResponseSingleRfidObserver = observer;
      if (this.isCordovaEnabled) {
        try {
          RFIDInterface.initSingleRead(
            result => {
              let parsedResult = JSON.parse(result);
              if (parsedResult.value) {
                let deserializedResponse = deserialize<TagReadResponse>(TagReadResponse, parsedResult);
                deserializedResponse.time = this._vizixService.vizixTime();
                observer.next(deserializedResponse);
              }
              /*else if (parsedResult.mode || parsedResult.mode == '') {
               this.triggerListenerObserver.next(parsedResult);
               }*/
            },
            error => {
              console.log('Entro error reader service');
              console.log(error);
              observer.error(JSON.parse(error));
            });
        } catch (error) {
          observer.error('Cordova not available or plugins are not setup correctly');
        }
      } else {
        this.cordovaNotAvailable(observer);
      }
    });
  }

  stopScan(): Observable<any> {
    console.log(this.tag, "stop scan");

    return Observable.create((observer: Observer<any>) => {
      if (this.isCordovaEnabled) {
        try {
          // This is a workarond, later this will be done in the library
          this.initGeiger(this.STOP_TAG).subscribe(() => {
            RFIDInterface.stopScan(
              result => {
                observer.next(result);
              },
              error => {
                observer.error(error);
                observer.complete();
              });
          }, error => {
            observer.error(JSON.parse(error));
            observer.complete();
          });
        } catch (error) {
          observer.error('Cordova not available or plugins are not setup correctly');
          observer.complete();
        }
      } else {
        this.cordovaNotAvailable(observer);
      }
    });
  }

  initGeiger(epc: string): Observable<any> {
    console.log(this.tag, "init geiger");

    if (this.simulatorService.isSimulatorEnabled) {
      //SimulatorConfiguration
      this.simulatorService.stopSimulator();
    }

    this.selectedTagForGeiger = epc;

    this.switchMode(ReaderMode.GEIGER);

    this.tagResponseGeigerObservable = Observable.create((observer: Observer<TagReadResponse>) => {
      this.tagResponseGeigerObserver = observer;

      if (!this.simulatorService.isSimulatorEnabled) {
        if (this.isCordovaEnabled) {
          //From plugin
          try {
            RFIDInterface.initGeiger(epc,
              result => {
                if (this.isTriggerPulled) {
                  let parsedResult = JSON.parse(result);
                  if (parsedResult.rssi || parsedResult.rssiPercentage) {
                    this.tagProximity = parsedResult.rssi ? parsedResult.rssi : parsedResult.rssiPercentage;
                    let deserializedResponse = deserialize<TagReadResponse>(TagReadResponse, parsedResult);
                    deserializedResponse.time = this._vizixService.vizixTime();
                    observer.next(deserializedResponse);
                  }
                }
                /*else if (parsedResult.mode || parsedResult.mode == '') {
                 this.triggerListenerObserver.next(parsedResult);
                 }*/
              },
              error => {
                observer.error(JSON.parse(error));
                observer.complete();
              });
          } catch (error) {
            observer.error('Cordova not available or plugins are not setup correctly');
            observer.complete();
          }
        } else {
          this.cordovaNotAvailable(observer);
        }
      } else {
        //From simulator
        if (epc != this.STOP_TAG) {
          this.simulatorService.getTagObservable().subscribe(
            (tagResponse: TagReadResponse) => {
              tagResponse.time = this._vizixService.vizixTime();
              //console.log(this.tag, "Simuate read: " + tagResponse.value);
              if (tagResponse.rssi || tagResponse.rssiPercentage) {
                this.tagProximity = tagResponse.rssi ? tagResponse.rssi : tagResponse.rssiPercentage;
              }
              observer.next(tagResponse);
            }, (error) => {
              console.log(this.tag, ' ERROR SimulatorConfiguration RFID', error);
              observer.error('ERROR SimulatorConfiguration RFID');
              observer.complete();
            }
          );
        }
      }
    });

    return this.tagResponseGeigerObservable;
  }

  isReaderAvailable(): Observable<any> { // TODO: DELETE, USE NEW METHODS
    console.log(this.tag, "is reader available");

    return Observable.create((observer: Observer<any>) => {
      if (this.isCordovaEnabled) {
        try {
          RFIDInterface.executeCommand(CODE_READER_IS_CONNECTED,
            null,
            result => {
              let response = JSON.parse(result);
              observer.next(response);
              observer.complete();
            },
            error => {
              observer.error(JSON.parse(error));
              observer.complete();
            });
        } catch (error) {
          observer.error('Cordova not available or plugins are not setup correctly');
        }
      } else {
        this.cordovaNotAvailable(observer);
      }
    });
  }

  isDeviceBarcode(): Observable<any> {
    console.log(this.tag, "is device barcode");

    return Observable.create((observer: Observer<any>) => {
      if (this.isCordovaEnabled) {
        try {
          RFIDInterface.isDeviceBarcode(
            result => {
              let response = JSON.parse(result);
              observer.next(response);
              observer.complete();
            },
            error => {
              observer.error(JSON.parse(error));
              observer.complete();
            });
        } catch (error) {
          observer.error('Cordova not available or plugins are not setup correctly');
          observer.complete();
        }
      } else {
        this.cordovaNotAvailable(observer);
      }
    });
  }

  initScanDeviceBarcode(): Observable<any> {
    return Observable.create((observer: Observer<any>) => {
      if (this.isCordovaEnabled) {
        try {
          RFIDInterface.initScanDeviceBarcode(
            result => {
              console.log(this.tag, "init scan device barcode");
              let response = JSON.parse(result);
              observer.next(response);
              // observer.complete();
            },
            error => {
              observer.error(JSON.parse(error));
              observer.complete();
            });
        } catch (error) {
          observer.error('Cordova not available or plugins are not setup correctly');
        }
      } else {
        this.cordovaNotAvailable(observer);
      }
    });
  }

  simulateTriggerPress(): Observable<any> {
    console.log(this.tag, "simulate trigger pressed");

    this.isTriggerPulled = true;

    return Observable.create((observer: Observer<any>) => {

      if (!this.simulatorService.isSimulatorEnabled) {
        if (this.isCordovaEnabled) {
          //Reader
          try {
            RFIDInterface.pressTrigger(
              result => {
                let parsedResult = JSON.parse(result);
                if (parsedResult.value) {
                  this.tagResponseMultiRfidObserver.next(parsedResult);
                }
                /*else if (parsedResult.mode || parsedResult.mode == '') {
                 this.triggerListenerObserver.next(parsedResult);
                 }*/
              },
              error => {
                if (!this.simulatorService.isSimulatorEnabled) {
                  observer.error(JSON.parse(error));
                  observer.complete();
                }
              });
          } catch (error) {
            observer.error('Cordova not available or plugins are not setup correctly');
          }
        } else {
          this.cordovaNotAvailable(observer);
        }
      }


      if (this.simulatorService.isSimulatorEnabled && this.isGeigerMode) {
        console.log(this.tag, ' Simulating Geiger Trigger Press');
        this.simulatorService.simulateGeiger(this.selectedTagForGeiger);
      } else if (this.simulatorService.isSimulatorEnabled && this.isMultiRfidMode) {
        console.log(this.tag, ' Simulating RFID Trigger Press');
        this.simulatorService.simulateReadings();
      } else if (this.simulatorService.isSimulatorEnabled && this.isInventoryMode) {
        console.log(this.tag, ' Simulating Inventory Trigger Press');
        this.simulatorService.simulateInventory(this.tagListForGeiger);
      }
    });
  }

  simulateTriggerRelease(): Observable<any> {
    console.log(this.tag, ' Simulating trigger release');
    this.isTriggerPulled = false;
    this.tagProximity = 0;

    return Observable.create((observer: Observer<any>) => {

      if (!this.simulatorService.isSimulatorEnabled) {
        if (this.isCordovaEnabled) {
          //Reader
          try {
            RFIDInterface.releaseTrigger(
              result => {
                console.log(this.tag, ' TRIGGER STATE RESPONSE', result);
                let parsedResult = JSON.parse(result);
                if (parsedResult.value) {
                  this.tagResponseMultiRfidObserver.next(parsedResult);
                }
                else observer.next(result);
                /*else if (parsedResult.mode || parsedResult.mode == '') {
                 this.triggerListenerObserver.next(parsedResult);
                 }*/
              },
              error => {
                if (!this.simulatorService.isSimulatorEnabled) {
                  observer.error(JSON.parse(error));
                  observer.complete();
                }
              });
          } catch (error) {
            observer.error('Cordova not available or plugins are not setup correctly');
          }
        } else {
          this.cordovaNotAvailable(observer);
        }
      }

      if (this.simulatorService.isSimulatorEnabled) {
        //SimulatorConfiguration
        this.simulatorService.stopSimulator();
      }
    });
  }

  setStatusListener(): Observable<any> {
    return Observable.create((observer: Observer<any>) => {

      if (!this.simulatorService.isSimulatorEnabled) {

        if (this.isCordovaEnabled) {
          //Reader
          try {
            RFIDInterface.setStatusListener(
              result => {
                let parsedResult = JSON.parse(result);
                observer.next(parsedResult);
              },
              error => {
                observer.error(JSON.parse(error));
                observer.complete();
              });
          } catch (error) {
            observer.error('Cordova not available or plugins are not setup correctly');
            observer.complete();
          }
        } else {
          this.cordovaNotAvailable(observer);
        }

      } else {
        //SimulatorConfiguration
        observer.next(true);
      }
    });
  }

  purgeTags(): Observable<any> {
    console.log(this.tag, "purge tags");

    return Observable.create((observer: Observer<any>) => {
      //Reader
      if (!this.simulatorService.isSimulatorEnabled) {

        if (this.isCordovaEnabled) {
          //Reader
          try {
            RFIDInterface.purgeTags(
              result => {
                console.log(this.tag, 'Purge tags response', result);
                let parsedResult = JSON.parse(result);
                observer.next(parsedResult);
              },
              error => {
                observer.error(JSON.parse(error));
                observer.complete();
              });
          } catch (error) {
            observer.error('Cordova not available or plugins are not setup correctly');
            observer.complete();
          }
        } else {
          this.cordovaNotAvailable(observer);
        }

      } else {
        //SimulatorConfiguration
        observer.next(true);
      }

      //SimulatorConfiguration
      this.simulatorService.clearCursor();
    });
  }

  stopScanCC(): Observable<any> {
    console.log(this.tag, "stop scan cc");

    this.switchMode(ReaderMode.NONE);

    return Observable.create((observer: Observer<any>) => {
      try {
        console.log(this.tag, ' STOPPING READER');
        // This is a workarond, later this will be done in the library
        this.initGeiger(this.STOP_TAG).subscribe();
        observer.next('Stop Success');
      } catch (error) {
        this.cordovaNotAvailable(observer);
      }
    });
  }

  cordovaNotAvailable(observer: Observer<any>) {
    console.log(this.tag, ' Cordova not available');
    if (observer != null) {
      observer.error('Cordova not available or plugins are not setup correctly');
    }
    // console.log(this.tag, ' Cordova not available or plugins are not setup correctly');
    console.log(this.tag, ' Cordova not available or plugins are not setup correctly');
  }

  getConfiguration(): Observable<ReaderThingConfiguration> {
    console.log(this.tag, "get configuration");

    return Observable.create((observer: Observer<ReaderThingConfiguration>) => {
      if (this.isCordovaEnabled) {
        RFIDInterface.getConfig(
          result => {
            console.log(this.tag, result);
            observer.next(JSON.parse(result));
            observer.complete();
          },
          error => {
            observer.error(JSON.parse(error));
            observer.complete();
          });
      } else {
        this.cordovaNotAvailable(observer);
      }
    });
  }


  setConfiguration(thingConfiguration: any): Observable<any> {  // TODO: DELETE, USE NEW METHODS
    console.log(this.tag, "set configuration");

    return Observable.create((observer: Observer<any>) => {
      if (this.isCordovaEnabled) {
        RFIDInterface.executeCommand(CODE_READER_SET_CONFIG,

          thingConfiguration
          ,
          result => {
            let response = JSON.parse(result);
            observer.next(response);

          },
          error => {
            observer.error(JSON.parse(error));

          });
      } else {
        this.cordovaNotAvailable(observer);
      }
    });
  }

  /*
   Method that changes configuration to barcode when radio button value=barcode
   */
  changeBarcodeMode(): Observable<any> { // TODO: DELETE, USE NEW METHODS
    console.log(this.tag, "change barcode mode");

    return Observable.create((observer: Observer<any>) => {
      this.setConfiguration(
        {
          "defaultMode": 2,
          "defaultScan": 1,
          "ReaderGeigerPower": 0,
          "ReaderMultiplePower": 1,
          "ReaderSinglePower": 20,
          "ReaderSoundEnabled": true,
          "ReaderVibrateEnabled": true,
          "qGeigerScan": 1,
          "qMultiScan": 5,
          "qSingleScan": 1,
          "powerMode": "db",
          "multiplePowerDB": 10,
          "singlePowerDB": 10,
          "selectGeigerScan": "No",
          "selectMultiScan": "No",
          "selectSingleScan": "Yes",
          "sessionGeigerScan": "S0",
          "sessionMultiScan": "S1",
          "sessionSingleScan": "S0",
          "targetGeigerScan": "B",
          "targetMultiScan": "A",
          "targetSingleScan": "A"
        }
      ).subscribe(
        (result) => {
          observer.next(result);
        }, (e) => {
          observer.error(e);
        }, () => {
          observer.complete();
        }
      );
    });
  }

  /*
   Method that changes configuration to rfid when radio button value=rfid
   */
  changeRFIDMode(): Observable<any> { // TODO: DELETE, USE NEW METHODS
    console.log(this.tag, "change rfid mode");

    return Observable.create((observer: Observer<any>) => {
      this.setConfiguration(
        {
          "defaultMode": 1,
          "defaultScan": 2,
          "ReaderGeigerPower": 0,
          "ReaderMultiplePower": 1,
          "ReaderSinglePower": 20,
          "ReaderSoundEnabled": true,
          "ReaderVibrateEnabled": true,
          "qGeigerScan": 1,
          "qMultiScan": 5,
          "qSingleScan": 1,
          "powerMode": "db",
          "multiplePowerDB": 10,
          "singlePowerDB": 10,
          "selectGeigerScan": "No",
          "selectMultiScan": "Yes",
          "selectSingleScan": "Yes",
          "sessionGeigerScan": "S0",
          "sessionMultiScan": "S1",
          "sessionSingleScan": "S0",
          "targetGeigerScan": "B",
          "targetMultiScan": "A",
          "targetSingleScan": "A"
        }
      ).subscribe(
        (result) => {
          console.log(this.tag, result);
          observer.next(result);
        }, (e) => {
          observer.error(e);
        }, () => {
          //observer.complete();
        }
      );
    });
  }

  changeOneRFIDMode(): Observable<any> { // TODO: DELETE, USE NEW METHODS
    return Observable.create((observer: Observer<any>) => {
      RFIDInterface.initOneReadRFID(result => {
          console.log(this.tag, ' raw:');
          console.log(this.tag, result);
          let response = JSON.parse(result);
          console.log(this.tag, ' json:');
          console.log(this.tag, response);
          observer.next(response);
        },
        error => {
          observer.error(JSON.parse(error));
          observer.complete();
        });
    });
  }

  changeAndWriteMode(targetEpc: string, newEpc: string, password: string): Observable<any> {
    return Observable.create((observer: Observer<any>) => {
      RFIDInterface.writeTag(targetEpc, newEpc, password, result => {
          let response = JSON.parse(result);
          console.log(this.tag, 'json:', response);
          observer.next(response);
        },
        error => {
          console.log(this.tag, "WRITING MODE", error);
          observer.error(JSON.parse(error));
          //observer.complete();
        });
    });
  }

  private switchMode(mode: ReaderMode) {

    switch (mode) {
      case ReaderMode.NONE: {
        console.log(this.tag, "switch mode NONE");
        this.isGeigerMode = false;
        this.isMultiRfidMode = false;
        this.isSingleRfidMode = false;
        this.isInventoryMode = false;
        break;
      }
      case ReaderMode.MULTI: {
        console.log(this.tag, "switch mode MULTI");
        this.isGeigerMode = false;
        this.isMultiRfidMode = true;
        this.isSingleRfidMode = false;
        this.isInventoryMode = false;
        break;
      }
      case ReaderMode.SINGLE: {
        console.log(this.tag, "switch mode SINGLE");
        this.isGeigerMode = false;
        this.isMultiRfidMode = false;
        this.isSingleRfidMode = true;
        this.isInventoryMode = false;
        break;
      }
      case ReaderMode.GEIGER: {
        console.log(this.tag, "switch mode GEIGER");
        this.isGeigerMode = true;
        this.isMultiRfidMode = false;
        this.isSingleRfidMode = false;
        this.isInventoryMode = false;
        break;
      }
      case ReaderMode.INVENTORY: {
        console.log(this.tag, "switch mode INVENTORY");
        this.isGeigerMode = false;
        this.isMultiRfidMode = false;
        this.isSingleRfidMode = false;
        this.isInventoryMode = true;
        break;
      }
      default: {
        console.log(this.tag, "switch mode NONE");
        this.isGeigerMode = false;
        this.isMultiRfidMode = false;
        this.isSingleRfidMode = false;
        this.isInventoryMode = false;
        break;
      }
    }
  }

  startStopWriteteMode(b: boolean): void {
    RFIDInterface.startStopWriteMode(b, result => {
        let response = JSON.parse(result);
        console.log(this.tag, 'json:', response);
      },
      error => {
        console.log(this.tag, "WRITING MODE", error);
      });
  }

  dynamicPowerEnable(b: boolean): void {
    RFIDInterface.dynamicPowerEnable(b, result => {
      let response = JSON.parse(result);
      console.log(this.tag, 'json', response);
    },
    error => {
      console.log(this.tag, "Power Dynamic", error);
    });
  }

}



// WEBPACK FOOTER //
// ./src/providers/reader.service.ts